import { Shimmer } from '@components'
import { getPeriodPageUrl, getVATPageUrl } from '@constants'
import { useBusinessContext } from '@context'
import { DashboardDeadlineType, IDashboardDeadline } from '@query'
import { formatDate } from '@utils'
import dayjs from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useDashboardOverview } from '../hooks/useDashboardOverview'
import { Card } from './shared/Card'

const DATE_CHIP_WIDTH = '2.5rem'
const DATE_CHIP_HEIGHT = '2.25rem'
const ITEM_MIN_HEIGHT = '4.75rem'
const PLACEHOLDER_ROW_COUNT = 3
const isOverdue = (deadline: IDashboardDeadline) => deadline.days_left < 0

type ChipTone = 'default' | 'urgent' | 'overdue' | 'muted'

const CalendarChip: React.FC<{ date: dayjs.Dayjs; tone: ChipTone }> = ({ date, tone }) => (
  <DateChip $tone={tone}>
    <ChipTop $tone={tone}>
      <i />
      <i />
    </ChipTop>
    <ChipDay>{date.format('D')}</ChipDay>
    <ChipMonth $tone={tone}>{date.format('MMM')}</ChipMonth>
  </DateChip>
)

const DeadlineItem: React.FC<{
  date: string
  to: string
  tone: ChipTone
  countdown: string
  title: string
  meta: string
}> = ({ date, to, tone, countdown, title, meta }) => (
  <DeadlineRow to={to}>
    <ChipColumn>
      <CalendarChip date={dayjs(date)} tone={tone} />
    </ChipColumn>
    <DeadlineText>
      <Countdown $tone={tone}>{countdown}</Countdown>
      <DeadlineTitle>{title}</DeadlineTitle>
      <DeadlineMeta>{meta}</DeadlineMeta>
    </DeadlineText>
  </DeadlineRow>
)

export const DeadlinesCard: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { data: overview, isLoading } = useDashboardOverview()
  const deadlines = overview?.deadlines ?? []

  const countdownFor = (deadline: IDashboardDeadline) =>
    isOverdue(deadline)
      ? t('dashboard.deadlines.daysAgo', { count: Math.abs(deadline.days_left) })
      : t('dashboard.deadlines.inDays', { count: deadline.days_left })

  const chipToneFor = (deadline: IDashboardDeadline): ChipTone => {
    if (isOverdue(deadline)) return 'overdue'
    if (deadline.days_left <= 5) return 'urgent'
    return 'default'
  }

  const linkFor = (deadline: IDashboardDeadline) => {
    switch (deadline.type) {
      case DashboardDeadlineType.VAT_RETURN:
        return getVATPageUrl(slug, { id: deadline.reference_id })
      case DashboardDeadlineType.PERIOD_REPORT:
        return getPeriodPageUrl(slug, { id: deadline.reference_id })
      case DashboardDeadlineType.EU_RECAPITULATIVE:
        return getVATPageUrl(slug, { id: deadline.reference_id })
      default: {
        const exhaustive: never = deadline.type
        return exhaustive
      }
    }
  }

  const metaFor = (deadline: IDashboardDeadline) => {
    switch (deadline.type) {
      case DashboardDeadlineType.VAT_RETURN:
        return t('dashboard.deadlines.meta.vatReturn', {
          from: deadline.start_date ? formatDate(deadline.start_date) : '',
          to: deadline.end_date ? formatDate(deadline.end_date) : ''
        })
      case DashboardDeadlineType.PERIOD_REPORT:
        return t('dashboard.deadlines.meta.periodReport')
      case DashboardDeadlineType.EU_RECAPITULATIVE:
        return t('dashboard.deadlines.meta.euRecapitulative', {
          from: deadline.start_date ? formatDate(deadline.start_date) : '',
          to: deadline.end_date ? formatDate(deadline.end_date) : ''
        })
      default: {
        const exhaustive: never = deadline.type
        return exhaustive
      }
    }
  }

  return (
    <RailCard title={t('dashboard.deadlines.title')} tooltip={t('dashboard.deadlines.tooltip')}>
      <Body>
        <Timeline $connect={isLoading || deadlines.length > 0}>
          <TodayRow>
            <ChipColumn>
              <CalendarChip date={dayjs()} tone="muted" />
            </ChipColumn>
            <DeadlineText>
              <TodayLabel>{t('dashboard.deadlines.today')}</TodayLabel>
              <TodayDate>{formatDate(dayjs())}</TodayDate>
            </DeadlineText>
          </TodayRow>

          {isLoading &&
            Array.from({ length: PLACEHOLDER_ROW_COUNT }).map((_, index) => (
              <PlaceholderRow key={index}>
                <ChipColumn>
                  <Shimmer
                    width={DATE_CHIP_WIDTH}
                    height={DATE_CHIP_HEIGHT}
                    borderRadius="0.5rem"
                  />
                </ChipColumn>
                <DeadlineText>
                  <CountdownSlot>
                    <Shimmer width="3.5rem" height="0.75rem" borderRadius="999px" />
                  </CountdownSlot>
                  <Shimmer width="9rem" height="0.875rem" />
                  <Shimmer width="6rem" height="0.75rem" />
                </DeadlineText>
              </PlaceholderRow>
            ))}

          {deadlines.map(deadline => (
            <DeadlineItem
              key={`${deadline.type}-${deadline.date}`}
              date={deadline.date}
              to={linkFor(deadline)}
              tone={chipToneFor(deadline)}
              countdown={countdownFor(deadline)}
              title={t(`dashboard.deadlines.type.${deadline.type}`)}
              meta={metaFor(deadline)}
            />
          ))}
        </Timeline>
      </Body>
    </RailCard>
  )
}

const RailCard = styled(Card)`
  min-width: 0;
  min-height: 0;
  align-self: stretch;
  height: auto;
  overflow: hidden;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.lg}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.md}rem;
`

const Timeline = styled.div<{ $connect: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-height: 0;

  ${({ $connect, theme }) =>
    $connect &&
    `
    &::before {
      content: '';
      position: absolute;
      top: calc(${DATE_CHIP_HEIGHT} / 2);
      bottom: calc(${ITEM_MIN_HEIGHT} / 2);
      left: calc(${DATE_CHIP_WIDTH} / 2 - 1px);
      width: 2px;
      background: ${theme.colors.borderSoft};
      pointer-events: none;
    }
  `}
`

const ChipColumn = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 0 0 ${DATE_CHIP_WIDTH};
  width: ${DATE_CHIP_WIDTH};
  min-width: ${DATE_CHIP_WIDTH};
  max-width: ${DATE_CHIP_WIDTH};
  height: ${DATE_CHIP_HEIGHT};
`

const rowLayout = css`
  display: flex;
  align-items: flex-start;
  flex: 0 0 auto;
  min-height: ${ITEM_MIN_HEIGHT};
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const TodayRow = styled.div`
  ${rowLayout}
`

const TodayLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;
`

const TodayDate = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-variant-numeric: tabular-nums;
`

const DeadlineRow = styled(Link)`
  ${rowLayout}
  color: inherit;
  text-decoration: none;

  &:hover {
    opacity: 0.82;
    color: inherit;
    text-decoration: none;
  }
`

const PlaceholderRow = styled.div`
  ${rowLayout}
`

const CountdownSlot = styled.span`
  align-self: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

const DateChip = styled.div<{ $tone: ChipTone }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  box-sizing: border-box;
  width: ${DATE_CHIP_WIDTH};
  min-width: ${DATE_CHIP_WIDTH};
  max-width: ${DATE_CHIP_WIDTH};
  min-height: ${DATE_CHIP_HEIGHT};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'overdue'
        ? theme.dashboard.critical
        : $tone === 'default'
        ? theme.colors.borderSoft
        : theme.colors.primary};
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const ChipTop = styled.div<{ $tone: ChipTone }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 0.375rem;
  background: ${({ theme, $tone }) =>
    $tone === 'overdue'
      ? theme.dashboard.critical
      : $tone === 'default'
      ? theme.dashboard.infoSoft
      : theme.colors.primary};

  i {
    width: 1.5px;
    height: 3px;
    border-radius: 1px;
    background: rgba(255, 255, 255, 0.8);
  }
`

const ChipDay = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0.125rem ${({ theme }) => theme.spacing.xxs}rem 0;
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme }) => theme.colors.menuBg};
  font-variant-numeric: tabular-nums;
`

const ChipMonth = styled.span<{ $tone: ChipTone }>`
  width: 100%;
  box-sizing: border-box;
  padding: 0 ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xxxs}rem;
  font-size: ${({ theme }) => theme.fontSize.px85}rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ theme, $tone }) =>
    $tone === 'overdue'
      ? theme.dashboard.critical
      : $tone === 'default'
      ? theme.colors.iconGray
      : theme.colors.primary};
`

const DeadlineText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  flex: 1;
  min-width: 0;
  min-height: ${DATE_CHIP_HEIGHT};
`

const DeadlineTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBg};
`

const Countdown = styled.span<{ $tone: ChipTone }>`
  align-self: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
  padding: 0.125rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 999px;
  background: ${({ theme, $tone }) =>
    $tone === 'overdue'
      ? theme.dashboard.criticalTint
      : $tone === 'urgent'
      ? theme.dashboard.cautionTint
      : theme.colors.neutralGray};
  color: ${({ theme, $tone }) =>
    $tone === 'overdue'
      ? theme.dashboard.critical
      : $tone === 'urgent'
      ? theme.dashboard.caution
      : theme.dashboard.textSubtle};
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  font-weight: 700;
  white-space: nowrap;
`

const DeadlineMeta = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
